import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, X } from 'lucide-react';
import posthog from 'posthog-js';
import { Button } from '@/components/ui/button';
import { ProgressStep } from './ProgressStep';
import { MobileProgressBar } from './MobileProgressBar';
import { OnboardingStep } from '@codemerlin/shared/constants/onboarding';
import { StepStatus } from '../../lib/onboarding-utils';
import { cn } from '@/lib/utils';

export interface StepData {
  id: OnboardingStep;
  label: string;
  status: StepStatus;
  isOptional?: boolean;
}

export interface ProgressTrackerProps {
  steps: StepData[];
  currentStepIndex: number;
  totalSteps: number;
  progressPercentage: number;
  currentStepLabel: string;
  onNavigate?: (stepId: OnboardingStep) => void;
  onBack?: (() => void) | undefined;
  onExit?: () => void;
  className?: string;
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  steps,
  currentStepIndex,
  totalSteps,
  progressPercentage,
  currentStepLabel,
  onNavigate,
  onBack,
  onExit,
  className,
}) => {
  const { t } = useTranslation('onboarding');

  // All hooks must be called before any conditional returns
  const currentStep = steps?.find((s) => s.status === 'current');

  const trackAction = useCallback(
    (action: string, properties: Record<string, unknown> = {}) => {
      try {
        posthog.capture(`onboarding_${action}`, {
          current_step: currentStep?.id,
          current_step_index: currentStepIndex,
          ...properties,
        });
      } catch (e) {
        // Ignore analytics errors
      }
    },
    [currentStep, currentStepIndex]
  );

  const handleBack = () => {
    trackAction('back_clicked');
    onBack?.();
  };

  const handleNavigate = (stepId: OnboardingStep) => {
    trackAction('navigation_clicked', { target_step: stepId });
    onNavigate?.(stepId);
  };

  const handleExit = () => {
    trackAction('exit_clicked');
    onExit?.();
  };

  /** Jump to any step in the wizard (Path, SCM, …) — same targets as header Back uses per step. */
  const handleStepClick = (stepId: OnboardingStep) => {
    if (!steps.some((s) => s.id === stepId)) return;
    handleNavigate(stepId);
  };

  // Edge case handling: Empty steps or invalid state (after hooks)
  if (!steps || steps.length === 0) {
    return null;
  }

  // Edge case handling: Single step flow - show simplified view
  if (totalSteps === 1) {
    return (
      <div className={cn('w-full border-b bg-background sticky top-0 z-50', className)}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20 gap-4">
            <div className="flex-1 text-center">
              <span className="text-sm text-muted-foreground">
                {t('wizard.status.count_of_total', { current: 1, total: 1 })}
              </span>
            </div>
            {onExit && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleExit}
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                aria-label={t('wizard.actions.exit')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Edge case handling: Invalid currentStepIndex
  if (currentStepIndex < 1 || currentStepIndex > totalSteps) {
    console.warn(`Invalid currentStepIndex: ${currentStepIndex}. Expected 1-${totalSteps}`);
  }

  return (
    <div className={cn('w-full border-b bg-background sticky top-0 z-50', className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Left: Back Action */}
          <div className="flex items-center w-16 md:w-20 shrink-0">
            {onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="text-muted-foreground hover:text-foreground -ml-2 h-9 px-2"
                aria-label={t('wizard.actions.back')}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                <span className="hidden sm:inline">{t('wizard.actions.back')}</span>
              </Button>
            )}
          </div>

          {/* Center: Desktop Stepper & Mobile Progress */}
          <div className="flex-1 flex items-center justify-center min-w-0">
            <nav
              className="hidden md:flex items-center py-2"
              aria-label={t('wizard.status.progress')}
            >
              <ol className="flex items-center">
                {steps.map((step, index) => (
                  <li key={step.id} className="flex items-center">
                    <ProgressStep
                      index={index}
                      label={step.label}
                      status={step.status}
                      isLast={index === steps.length - 1}
                      onClick={() => handleStepClick(step.id)}
                    />
                  </li>
                ))}
              </ol>
            </nav>

            <MobileProgressBar
              className="md:hidden"
              currentStepIndex={currentStepIndex}
              totalSteps={totalSteps}
              progressPercentage={progressPercentage}
              currentStepLabel={currentStepLabel}
            />
          </div>

          {/* Right: Exit */}
          <div className="flex items-center justify-end w-10 shrink-0 gap-2">
            {onExit && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleExit}
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                aria-label={t('wizard.actions.exit')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
